import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  AssignmentTurnedIn as AssignIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ModernCard from '../components/Common/ModernCard';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import ModernLoader from '../components/Common/ModernLoader';
import api from '../services/api';
import { logger } from '../utils/logger';

interface Location {
  location_id: string;
  company_id: string;
  company_name?: string;
  external_id: string;  // PassKit location ID
  name: string;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  lock_screen_message?: string;
  position?: number;
  is_active: boolean;
  passkit_sync_status?: string; // 'synced' | 'not_found' | 'error'
  created_at: string;
}

interface Tier {
  id: string;
  name: string;  // Tier name
  tier_name: string;
  tier_type: string;
  tier_index: number;
  program_id: string;
  program_name: string;
  company_name?: string;
  passkit_tier_id: string;
  pass_template_id: string;  // PassKit template ID
  location_count?: number; // Trenutni broj lokacija u tier-u
  location_ids?: string[]; // Lista location external_id-jeva
}

const Locations: React.FC = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const [searchName, setSearchName] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [locationLimit, setLocationLimit] = useState({ current: 0, max: 0 });

  // Assign to Tier state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableTiers, setAvailableTiers] = useState<Tier[]>([]);
  const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [tiersLoading, setTiersLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    altitude: '',
    lock_screen_message: '',
    position: '0'
  });

  const fetchLocationLimit = useCallback(async () => {
    try {
      const response = await api.get('/companies/me/');
      const companyData = response.data;
      const tier = companyData.tier || 'free';
      const limits: Record<string, number> = {
        'free': 2,
        'basic': 5,
        'professional': 20,
        'enterprise': 999
      };
      setLocationLimit({ current: locations.length, max: limits[tier] || 2 });
    } catch (err) {
      logger.error('Error fetching location limit:', err);
    }
  }, [locations.length]);

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      // Admin sees all locations, regular users see only their company's locations
      const endpoint = user?.role === 'admin' ? '/locations' : '/locations/me';

      const response = await api.get(endpoint);
      const data = response.data;
      // Map external_id from backend (no transformation needed now)
      setLocations(data);
      setLocationLimit(prev => ({ ...prev, current: data.length }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load locations');
      logger.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchLocations();
    fetchLocationLimit();
  }, [fetchLocations, fetchLocationLimit]);

  // Filter locations based on search
  const filteredLocations = locations.filter(location => {
    const matchesName = searchName === '' || location.name?.toLowerCase().includes(searchName.toLowerCase());
    const matchesCompany = searchCompany === '' || location.company_name?.toLowerCase().includes(searchCompany.toLowerCase());
    return matchesName && matchesCompany;
  });

  const handleOpenDialog = (location?: Location) => {
    if (location) {
      setIsEditing(true);
      setSelectedLocation(location);
      setFormData({
        name: location.name,
        latitude: location.latitude?.toString() || '',
        longitude: location.longitude?.toString() || '',
        altitude: location.altitude?.toString() || '',
        lock_screen_message: location.lock_screen_message || '',
        position: location.position?.toString() || '0'
      });
    } else {
      setIsEditing(false);
      setSelectedLocation(null);
      setFormData({
        name: '',
        latitude: '',
        longitude: '',
        altitude: '',
        lock_screen_message: '',
        position: '0'
      });
    }
    setDialogOpen(true);
    setFormError(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormError(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError('Naziv lokacije je obavezan');
      return;
    }

    setSaveLoading(true);
    setFormError(null);

    try {
      const payload = {
        ...formData,
        latitude: formData.latitude ? Number.parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? Number.parseFloat(formData.longitude) : null,
        altitude: formData.altitude ? Number.parseFloat(formData.altitude) : null,
        position: formData.position ? Number.parseInt(formData.position, 10) : null
      };

      if (isEditing) {
        await api.put(`/locations/${selectedLocation?.location_id}`, payload);
      } else {
        await api.post('/locations', payload);
      }

      await fetchLocations();
      handleCloseDialog();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || err.message || 'Failed to save location');
      logger.error('Error saving location:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (locationId: string) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu lokaciju?')) {
      return;
    }

    try {
      await api.delete(`/locations/${locationId}`);

      await fetchLocations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete location');
      logger.error('Error deleting location:', err);
    }
  };

  // ========== ASSIGN TO TIER FUNCTIONS ==========



  const handleOpenAssignDialog = async (location: Location) => {
    setSelectedLocation(location);
    setAssignError(null);
    setSelectedTierIds([]);
    setAssignDialogOpen(true);

    // Fetch available membership tiers for the company
    setTiersLoading(true);
    try {
      const response = await api.get('/membership/tiers');
      if (response.data) {
        const tiers = response.data.tiers || [];
        setAvailableTiers(tiers);

        // Pre-select tiers that already contain this location
        const tiersWithThisLocation = tiers
          .filter((tier: Tier) => tier.location_ids?.includes(location.external_id))
          .map((tier: Tier) => tier.id);

        if (tiersWithThisLocation.length > 0) {
          setSelectedTierIds(tiersWithThisLocation);
        }
      }
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to load tiers');
      logger.error('Error fetching tiers:', err);
    } finally {
      setTiersLoading(false);
    }
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedLocation(null);
    setSelectedTierIds([]);
    setAssignError(null);
  };

  const handleTierToggle = (tierId: string, event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedTierIds(prev =>
      prev.includes(tierId)
        ? prev.filter(id => id !== tierId)
        : [...prev, tierId]
    );
  };

  const handleAssignToTiers = async () => {
    if (!selectedLocation) {
      setAssignError('Lokacija nije selektovana');
      return;
    }

    setAssignLoading(true);
    setAssignError(null);

    try {
      // Determine which tiers need to be assigned vs unassigned
      const currentlyAssignedTierIds = availableTiers
        .filter(tier => tier.location_ids?.includes(selectedLocation.external_id))
        .map(tier => tier.id);

      // Tiers to assign: selected but not currently assigned
      const tiersToAssign = selectedTierIds.filter(id => !currentlyAssignedTierIds.includes(id));

      // Tiers to unassign: currently assigned but not selected
      const tiersToUnassign = currentlyAssignedTierIds.filter(id => !selectedTierIds.includes(id));

      // Assign new tiers
      if (tiersToAssign.length > 0) {
        const assignTiers = tiersToAssign.map(tierId => {
          const tier = availableTiers.find(t => t.id === tierId);
          if (!tier) throw new Error(`Tier ${tierId} not found`);
          return {
            tier_id: tier.id,
            tier_name: tier.name,
            template_id: tier.pass_template_id
          };
        });

        await api.post(
          `/locations/${selectedLocation.location_id}/assign-to-tiers`,
          {
            tiers: assignTiers,
            location_name: selectedLocation.name,
            lock_screen_message: selectedLocation.lock_screen_message || null
          }
        );
      }

      // Unassign removed tiers
      if (tiersToUnassign.length > 0) {
        const unassignTiers = tiersToUnassign.map(tierId => {
          const tier = availableTiers.find(t => t.id === tierId);
          if (!tier) throw new Error(`Tier ${tierId} not found`);
          return {
            tier_id: tier.id,
            tier_name: tier.name,
            template_id: tier.pass_template_id
          };
        });

        await api.post(
          `/locations/${selectedLocation.location_id}/unassign-from-tiers`,
          {
            tiers: unassignTiers,
            location_name: selectedLocation.name,
            lock_screen_message: selectedLocation.lock_screen_message || null
          }
        );
      }

      // Refresh locations list to show updated state
      await fetchLocations();

      // Show success message
      const assignedCount = tiersToAssign.length;
      const unassignedCount = tiersToUnassign.length;
      let successMsg = '';
      if (assignedCount > 0 && unassignedCount > 0) {
        successMsg = `Lokacija dodeljena na ${assignedCount} i uklonjena sa ${unassignedCount} tier(ova)`;
      } else if (assignedCount > 0) {
        successMsg = `Lokacija uspešno dodeljena na ${assignedCount} tier(ova)`;
      } else if (unassignedCount > 0) {
        successMsg = `Lokacija uspešno uklonjena sa ${unassignedCount} tier(ova)`;
      } else {
        successMsg = 'Nema promena';
      }

      setError(null);
      setSuccess(successMsg);

      handleCloseAssignDialog();
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to update location assignment');
      logger.error('Error updating location assignment:', err);
    } finally {
      setAssignLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <ModernLoader size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: 'linear-gradient(135deg, #f7faff 0%, #dfeafc 50%, #abc3ea 100%)' }}>
      <ResponsivePageHeader
        title="Lokacije"
        subtitle="Upravljajte lokacijama vaše kompanije"
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Dodaj lokaciju
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Limit Display */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Pretraži po imenu"
          variant="outlined"
          size="small"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          sx={{ minWidth: 250 }}
          placeholder="Unesite naziv lokacije..."
        />
        {user?.role === 'admin' && (
          <TextField
            label="Pretraži po kompaniji"
            variant="outlined"
            size="small"
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
            sx={{ minWidth: 200 }}
            placeholder="Naziv kompanije..."
          />
        )}
        <Box sx={{ ml: 'auto' }}>
          {user?.role === 'admin' ? (
            <Chip
              label={`Ukupno lokacija: ${locationLimit.current}`}
              color="info"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
            />
          ) : (
            <Chip
              label={`Lokacije: ${locationLimit.current} / ${locationLimit.max}`}
              color={locationLimit.current >= locationLimit.max ? 'error' : 'primary'}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {filteredLocations.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <LocationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {locations.length === 0 ? 'Nemate dodatu ni jednu lokaciju' : 'Nema rezultata pretrage'}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {locations.length === 0
                    ? 'Dodajte lokacije kako bi korisnici mogli lakše da pronađu vaše pass-ove'
                    : 'Pokušajte da promenite kriterijume pretrage'}
                </Typography>
                {locations.length === 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                  >
                    Dodaj prvu lokaciju
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredLocations.map((location) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={location.location_id}>
              <ModernCard>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box flex={1}>
                      <Typography variant="h6" gutterBottom>
                        {location.name || 'Bez naziva'}
                      </Typography>
                      {user?.role === 'admin' && location.company_name && (
                        <Chip
                          label={location.company_name}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAssignDialog(location)}
                        color="primary"
                        title="Dodeli tier-ovima"
                      >
                        <AssignIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDialog(location)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(location.location_id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Stack spacing={1}>
                    {(location.latitude || location.longitude) && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">
                          Lat: {location.latitude || 'N/A'}, Lon: {location.longitude || 'N/A'}
                          {location.altitude && `, Alt: ${location.altitude}m`}
                        </Typography>
                      </Box>
                    )}

                    {location.lock_screen_message && (
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <PhoneIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          "{location.lock_screen_message}"
                        </Typography>
                      </Box>
                    )}

                    {location.position !== undefined && location.position !== null && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          Pozicija: {location.position}
                        </Typography>
                      </Box>
                    )}

                    {location.created_at && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                          Kreirano: {new Date(location.created_at).toLocaleDateString('sr-RS')}
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={location.is_active ? 'Aktivna' : 'Neaktivna'}
                      size="small"
                      color={location.is_active ? 'success' : 'default'}
                    />
                    {location.passkit_sync_status === 'not_found' && (
                      <Chip
                        label="⚠️ NE POSTOJI U PASSKIT"
                        size="small"
                        color="error"
                        sx={{
                          fontWeight: 'bold',
                          animation: 'pulse 2s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.7 }
                          }
                        }}
                      />
                    )}
                    {location.passkit_sync_status === 'error' && (
                      <Chip
                        label="⚠️ GREŠKA SINHRONIZACIJE"
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 'bold' }}
                      />
                    )}
                    {location.company_name && (
                      <Chip
                        label={location.company_name}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    )}
                  </Box>
                </CardContent>
              </ModernCard>
            </Grid>
          ))
        )}
      </Grid>

      {/* Add/Edit Location Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {isEditing ? 'Izmeni lokaciju' : 'Dodaj novu lokaciju'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={2}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 1 }}>
              Unesite naziv lokacije i koordinate. Podaci će biti sačuvani u sistemu.
            </Alert>

            <TextField
              label="Naziv lokacije *"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={saveLoading}
              helperText="Obavezno polje - naziv lokacije"
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Latitude *"
                  fullWidth
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  disabled={saveLoading}
                  helperText="Geografska širina (obavezno)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Longitude *"
                  fullWidth
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  disabled={saveLoading}
                  helperText="Geografska dužina (obavezno)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Altitude"
                  fullWidth
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.altitude}
                  onChange={(e) => setFormData({ ...formData, altitude: e.target.value })}
                  disabled={saveLoading}
                  helperText="Visina u metrima (opciono)"
                />
              </Grid>
            </Grid>

            <TextField
              label="Lock Screen Message"
              fullWidth
              multiline
              rows={2}
              value={formData.lock_screen_message}
              onChange={(e) => setFormData({ ...formData, lock_screen_message: e.target.value })}
              disabled={saveLoading}
              helperText="Poruka koja se prikazuje kada je korisnik blizu lokacije (opciono)"
              placeholder="Dobrodošli u našu prodavnicu!"
            />

            <TextField
              label="Pozicija (redosled)"
              fullWidth
              type="number"
              inputProps={{ min: 0 }}
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              disabled={saveLoading}
              helperText="Redni broj za sortiranje - manji broj = viši prioritet (opciono, 0 = prvi)"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined" disabled={saveLoading}>
            Otkaži
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saveLoading}
            startIcon={saveLoading ? <ModernLoader size={20} /> : null}
          >
            {saveLoading ? 'Čuvanje...' : isEditing ? 'Sačuvaj' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to Tier Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Dodeli lokaciju tier-ovima
            </Typography>
            <IconButton onClick={handleCloseAssignDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={2}>
            {assignError && (
              <Alert severity="error" onClose={() => setAssignError(null)}>
                {assignError}
              </Alert>
            )}

            <Alert severity="info">
              Izaberite tier-ove na koje želite da dodate lokaciju <strong>{selectedLocation?.name}</strong>.
              Odčekirajte tier-ove sa kojih želite da je uklonite. Svaki tier može imati maksimalno 10 lokacija.
            </Alert>

            {tiersLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : availableTiers.length === 0 ? (
              <Alert severity="warning">
                Trenutno nemate kreiranih tier-ova. Prvo kreirajte tier u okviru programa.
              </Alert>
            ) : (
              <FormGroup>
                {availableTiers.map((tier) => {
                  const locationCount = tier.location_count || 0;
                  const isMaxed = locationCount >= 10;
                  const hasThisLocation = tier.location_ids?.includes(selectedLocation?.external_id || '');

                  return (
                    <FormControlLabel
                      key={tier.id}
                      onClick={(e) => e.stopPropagation()}
                      control={
                        <Checkbox
                          checked={selectedTierIds.includes(tier.id)}
                          onChange={(e) => handleTierToggle(tier.id, e)}
                          disabled={isMaxed && !selectedTierIds.includes(tier.id)}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography>
                            {tier.name} {tier.company_name && `(${tier.company_name})`}
                          </Typography>
                          <Chip
                            label={`${locationCount}/10`}
                            size="small"
                            color={isMaxed ? 'error' : 'default'}
                          />
                          {hasThisLocation && (
                            <Chip
                              label="Već dodeljena"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </FormGroup>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseAssignDialog}
            variant="outlined"
            disabled={assignLoading}
          >
            Otkaži
          </Button>
          <Button
            onClick={handleAssignToTiers}
            variant="contained"
            disabled={assignLoading}
            startIcon={assignLoading ? <ModernLoader size={20} /> : <AssignIcon />}
          >
            {assignLoading ? 'Čuvam promene...' : 'Sačuvaj promene'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Notifications */}
      <Snackbar
        open={success !== null}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Locations;
